import React, { useRef, useState } from 'react';
import { useDetectScroll } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  Chip,
  FooterActionBar,
  Icon,
  ListRow,
  Menu,
  ModalIsland,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import {
  type ConfirmCommand,
  ConfirmOption,
} from '../../../shared/components/ConfirmOption';
import { type IdentityProvider } from '../../../shared/types';
import {
  type InvertedLocalize,
  type LocalizationMessages,
  useInvertedLocalize,
} from '../../../shared/utils/localization';
import { getIdpRedirectUrl } from '../../login-form/data-sources/api';
import {
  deleteProvider,
  enableProvider,
  getSpMetadata,
  setTestMode,
} from '../data-sources';
import { useProviderEdit } from '../hooks';
import { Providers } from '../types';

import { ProviderEditView } from './ProviderEditView';

import './SSOListView.css';

interface Props {
  providers: IdentityProvider[];
  reloadProviders: () => void;
}

type ProviderStatus = 'active' | 'testMode' | 'inactive';

/** Derives the display status from the provider's enabled/testMode flags */
function getProviderStatus(provider: IdentityProvider): ProviderStatus {
  if (!provider.isEnabled) {
    return 'inactive';
  }

  if (provider.isTestMode) {
    return 'testMode';
  }

  return 'active';
}

const statusConfig: Record<
  ProviderStatus,
  { color: 'success' | 'warning' | 'neutral'; labelKey: LocalizationMessages }
> = {
  active: { color: 'success', labelKey: 'Active' },
  testMode: { color: 'warning', labelKey: 'Test Mode' },
  inactive: { color: 'neutral', labelKey: 'Inactive' },
};

type MenuAction =
  | 'activate'
  | 'test_mode'
  | 'deactivate'
  | 'sp_metadata'
  | 'delete';

/**
 * Returns the context menu options based on the provider's current status.
 * - Inactive: can activate, enable test mode, or delete
 * - Test Mode: can activate (promote) or deactivate
 * - Active: can deactivate
 */
function getMenuOptions(
  status: ProviderStatus,
  l: InvertedLocalize
): { label: string; value: MenuAction }[] {
  const spMetadata = {
    label: l('SP Meta Data'),
    value: 'sp_metadata' as const,
  };
  const deleteOption = { label: l('Delete'), value: 'delete' as const };

  switch (status) {
    case 'inactive':
      return [
        { label: l('Activate'), value: 'activate' },
        { label: l('Enable Test Mode'), value: 'test_mode' },
        spMetadata,
        deleteOption,
      ];
    case 'testMode':
      return [
        { label: l('Activate'), value: 'activate' },
        { label: l('Deactivate'), value: 'deactivate' },
        spMetadata,
        deleteOption,
      ];
    case 'active':
      return [
        { label: l('Deactivate'), value: 'deactivate' },
        spMetadata,
        deleteOption,
      ];
    default:
      return [spMetadata, deleteOption];
  }
}

/** Show list of available providers */
export function SSOListView({
  providers,
  reloadProviders,
}: Props): JSX.Element {
  const l = useInvertedLocalize();
  const {
    activeProvider,
    canSave,
    isSaving,
    isNew,
    saveProvider,
    cancelProviderEdit,
    editProvider,
    newProvider,
    saml,
  } = useProviderEdit(providers, reloadProviders);
  const sectionRef = useRef(null);
  const { isScrolled }: { isScrolled: boolean } = useDetectScroll(
    null,
    sectionRef
  );
  const [confirmRequest, setConfirmRequest] = useState<
    ConfirmCommand | undefined
  >(undefined);
  const clearRequest = () => setConfirmRequest(undefined);
  const [copiedAlias, setCopiedAlias] = useState<string | null>(null);
  const [copyingAlias, setCopyingAlias] = useState<string | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Copies the IdP login URL to the clipboard and shows a brief confirmation */
  const handleCopyLoginUrl = (alias: string) => {
    setCopyingAlias(alias);
    getIdpRedirectUrl({ alias })
      .then((redirectUrl) =>
        navigator.clipboard.writeText(redirectUrl).then(() => {
          if (copiedTimerRef.current) {
            clearTimeout(copiedTimerRef.current);
          }

          setCopyingAlias(null);
          setCopiedAlias(alias);
          copiedTimerRef.current = setTimeout(() => {
            setCopiedAlias(null);
            copiedTimerRef.current = null;
          }, 3000);
        })
      )
      .catch(() => {
        // Where to show error message?
      });
  };

  const handleContextMenu = (
    action: MenuAction,
    provider: IdentityProvider
  ) => {
    const providerLabel = provider.name || provider.alias;

    switch (action) {
      case 'activate':
        setConfirmRequest({
          title: l('Activate Provider'),
          description: l(
            'Are you sure you want to activate {providerName}? It will be used for login.',
            { providerName: providerLabel }
          ),
          onConfirm: () => {
            enableProvider(provider.alias, true)
              .then(() => reloadProviders())
              .catch(() => {
                // Where to show error message?
              });
          },
        });
        break;
      case 'test_mode':
        setTestMode(provider.alias, true)
          .then(() => reloadProviders())
          .catch(() => {
            // Where to show error message?
          });
        break;
      case 'deactivate':
        setConfirmRequest({
          title: l('Deactivate Provider'),
          description: l(
            'Are you sure you want to deactivate {providerName}?',
            { providerName: providerLabel }
          ),
          onConfirm: () => {
            enableProvider(provider.alias, false)
              .then(() => reloadProviders())
              .catch(() => {
                // Where to show error message?
              });
          },
        });
        break;
      case 'sp_metadata':
        getSpMetadata(provider.alias)
          .then((url: string) => {
            window.open(url, '_blank');
          })
          .catch(() => {
            // Where to show error message?
          });
        break;
      case 'delete':
        setConfirmRequest({
          title: l('Delete Provider'),
          captcha: provider.alias,
          description: l('Are you sure you want to delete {providerName}?', {
            providerName: providerLabel,
          }),
          onConfirm: () => {
            deleteProvider(provider.alias)
              .then(() => reloadProviders())
              .catch(() => {
                // Where to show error message?
              });
          },
        });
        break;
    }
  };

  return (
    <Box className='Auth_SSOListView'>
      <Box className='Auth_SSOListView_addProviderButton'>
        <Menu
          placement='bottom-end'
          onChange={({ value }: { value: Providers }) => {
            newProvider(value);
          }}
          trigger={
            <Button kind='outlined' leftIcon={<Icon kind='Add' />}>
              {l('Add Provider')}
            </Button>
          }
          options={[
            {
              label: Providers.SAML,
              value: Providers.SAML,
            },
          ]}
        />
      </Box>
      {providers.map((provider) => {
        const status = getProviderStatus(provider);
        const { color, labelKey } = statusConfig[status];
        const menuOptions = getMenuOptions(status, l);
        const isCopied = copiedAlias === provider.alias;
        const isCopying = copyingAlias === provider.alias;

        return (
          <ListRow
            key={provider.alias}
            isCheckboxDisabled
            leadingComp={<Icon kind='Entitlement' />}
            centerContent={
              <Box>
                <Typography kind='body3' className='Auth_SSOListView_protocol'>
                  {provider.protocol}
                </Typography>
                <Typography kind='h3' className='Auth_SSOListView_name'>
                  {provider.name}
                </Typography>
              </Box>
            }
            trailingContent={
              <Box className='Auth_SSOListView_trailingContent'>
                <Chip
                  kind='subtleFilled'
                  isInteractionDisabled
                  color={color}
                  label={l(labelKey)}
                  className='Auth_SSOListView_status'
                />
                {status === 'testMode' && (
                  <Tooltip
                    text={
                      isCopied
                        ? l('Copied! To test, open in incognito window')
                        : l('Copy Login URL')
                    }
                    placement='bottom'
                    isPortalEnabled>
                    <ButtonIcon
                      kind='outlinedSecondary'
                      size='large'
                      icon={
                        <Icon
                          kind={
                            // eslint-disable-next-line no-nested-ternary
                            isCopying
                              ? 'Hourglass'
                              : isCopied
                              ? 'Check'
                              : 'Copy'
                          }
                        />
                      }
                      ariaLabel={l('Copy Login URL')}
                      onClick={() => handleCopyLoginUrl(provider.alias)}
                    />
                  </Tooltip>
                )}
                <ButtonIcon
                  kind='outlinedSecondary'
                  size='large'
                  icon={<Icon kind='Edit' />}
                  ariaLabel={l('Edit')}
                  onClick={() => editProvider(provider)}
                />
                <Menu
                  isPortalEnabled
                  onChange={({ value }: { value: MenuAction }) => {
                    handleContextMenu(value, provider);
                  }}
                  trigger={
                    <ButtonIcon
                      kind='ghost'
                      size='medium'
                      icon={<Icon kind='MoreVertical' />}
                      ariaLabel={l('Options')}
                    />
                  }
                  options={menuOptions}
                />
              </Box>
            }
          />
        );
      })}
      <ModalIsland
        title={
          isNew
            ? l('Add {ssoProvider} Provider', {
                ssoProvider: activeProvider ?? '',
              })
            : l('Modify {ssoProvider} Provider', {
                ssoProvider: activeProvider ?? '',
              })
        }
        subtitle={l(
          'Enterprise login protocol for single sign-on across multiple apps via an identity provider.'
        )}
        className='Auth_SSOListView_modal'
        isOpen={activeProvider !== null}
        onClose={cancelProviderEdit}
        sectionRef={sectionRef}
        contentBody={
          <ProviderEditView activeProvider={activeProvider} saml={saml} />
        }
        FooterActionBarComp={
          <FooterActionBar
            size='large'
            isScrolled={isScrolled}
            className='Auth_SSOListView_modalFooter'>
            <Button
              kind='filled'
              onClick={saveProvider}
              isLoading={isSaving}
              isDisabled={!canSave}>
              {l('Save')}
            </Button>
          </FooterActionBar>
        }
      />
      <ConfirmOption
        confirmRequest={confirmRequest}
        clearRequest={clearRequest}
      />
    </Box>
  );
}
